import { LivenessService, LivenessVerificationResult } from './LivenessService';
import FaceDetectionService from './FaceDetectionService';

export interface AttendanceMarkRequest {
  punchCode: string;
  latitude: number;
  longitude: number;
  deviceType: string;
  imageBase64?: string; // Add image data for face detection
}

export interface AttendanceMarkResult {
  success: boolean;
  message: string;
  data?: any;
  livenessResult?: LivenessVerificationResult;
  faceDetectionResult?: any; // Add face detection result
}

export class EnhancedAttendanceService {
  private static API_BASE_URL = "https://faceapp.cditproject.org";

  /**
   * Complete attendance marking flow with face detection and liveness verification
   * @param attendanceData - Attendance data including punch code, location, device type, and optional image
   * @returns Promise<AttendanceMarkResult>
   */
  static async markAttendanceWithFaceDetection(attendanceData: AttendanceMarkRequest): Promise<AttendanceMarkResult> {
    try {
      console.log("EnhancedAttendanceService: Starting attendance marking with face detection...");
      
      // Validate input data
      if (!attendanceData.punchCode || attendanceData.punchCode.trim() === "") {
        throw new Error("Punch code is required");
      }
      
      if (!attendanceData.latitude || !attendanceData.longitude) {
        throw new Error("Location coordinates are required");
      }

      let faceDetectionResult = null;

      // Step 1: If image is provided, use face detection
      if (attendanceData.imageBase64) {
        console.log("EnhancedAttendanceService: Step 1 - Performing face detection...");
        faceDetectionResult = await FaceDetectionService.detectFaces(
          attendanceData.imageBase64,
          attendanceData.punchCode,
          attendanceData.latitude,
          attendanceData.longitude
        );
        
        if (!faceDetectionResult.success) {
          console.error("EnhancedAttendanceService: Face detection failed:", faceDetectionResult.backendResponse?.message);
          return {
            success: false,
            message: `Face detection failed: ${faceDetectionResult.backendResponse?.message || 'Unknown error'}`,
            faceDetectionResult,
          };
        }

        console.log("EnhancedAttendanceService: Face detection successful");
        
        // If face detection was successful and handled attendance marking, return the result
        if (faceDetectionResult.backendResponse?.status === 'success') {
          return {
            success: true,
            message: faceDetectionResult.backendResponse.message || 'Attendance marked successfully with face detection',
            data: faceDetectionResult.backendResponse.data,
            faceDetectionResult,
          };
        }
      }

      // Step 2: Fallback to liveness verification if no image or face detection didn't handle attendance
      console.log("EnhancedAttendanceService: Step 2 - Performing liveness verification...");
      const livenessResult = await LivenessService.completeLivenessFlow();
      
      if (!livenessResult.success) {
        console.error("EnhancedAttendanceService: Liveness verification failed:", livenessResult.message);
        return {
          success: false,
          message: `Liveness verification failed: ${livenessResult.message}`,
          livenessResult,
          faceDetectionResult,
        };
      }

      console.log("EnhancedAttendanceService: Liveness verification successful, proceeding to attendance marking...");

      // Step 3: Mark attendance after successful verification
      const attendanceResult = await this.markAttendance(attendanceData);
      
      return {
        ...attendanceResult,
        livenessResult,
        faceDetectionResult,
      };
    } catch (error) {
      console.error("EnhancedAttendanceService: Error in attendance marking:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Complete attendance marking flow with liveness verification (original method)
   * @param attendanceData - Attendance data including punch code, location, and device type
   * @returns Promise<AttendanceMarkResult>
   */
  static async markAttendanceWithLiveness(attendanceData: AttendanceMarkRequest): Promise<AttendanceMarkResult> {
    try {
      console.log("EnhancedAttendanceService: Starting attendance marking with liveness...");
      
      // Validate input data
      if (!attendanceData.punchCode || attendanceData.punchCode.trim() === "") {
        throw new Error("Punch code is required");
      }
      
      if (!attendanceData.latitude || !attendanceData.longitude) {
        throw new Error("Location coordinates are required");
      }

      // Step 1: Complete liveness verification flow
      console.log("EnhancedAttendanceService: Step 1 - Performing liveness verification...");
      const livenessResult = await LivenessService.completeLivenessFlow();
      
      if (!livenessResult.success) {
        console.error("EnhancedAttendanceService: Liveness verification failed:", livenessResult.message);
        return {
          success: false,
          message: `Liveness verification failed: ${livenessResult.message}`,
          livenessResult,
        };
      }

      console.log("EnhancedAttendanceService: Liveness verification successful, proceeding to attendance marking...");

      // Step 2: Mark attendance after successful liveness verification
      const attendanceResult = await this.markAttendance(attendanceData);
      
      return {
        ...attendanceResult,
        livenessResult,
      };
    } catch (error) {
      console.error("EnhancedAttendanceService: Error in attendance marking with liveness:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Mark attendance after liveness verification
   * @param attendanceData - Attendance data
   * @returns Promise<AttendanceMarkResult>
   */
  private static async markAttendance(attendanceData: AttendanceMarkRequest): Promise<AttendanceMarkResult> {
    try {
      console.log("EnhancedAttendanceService: Marking attendance...");

      // const response = await fetch(`${this.API_BASE_URL}/attendance/mark`, {
      const response = await fetch(`${this.API_BASE_URL}/api/verify-candidate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          punch_code: attendanceData.punchCode.trim(),
          latitude: attendanceData.latitude,
          longitude: attendanceData.longitude,
          device_type: attendanceData.deviceType,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("EnhancedAttendanceService: Attendance marking failed:", errorText);
        
        let errorMessage = `Attendance marking failed: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (parseError) {
          // Keep default error message
        }

        return {
          success: false,
          message: errorMessage,
        };
      }

      const result = await response.json();
      console.log("EnhancedAttendanceService: Attendance marked successfully:", result);

      return {
        success: true,
        message: result.message || 'Attendance marked successfully',
        data: result,
      };
    } catch (error) {
      console.error("EnhancedAttendanceService: Error marking attendance:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error during attendance marking',
      };
    }
  }

  /**
   * Mark entry with face detection and liveness verification
   * @param punchCode - Employee punch code
   * @param latitude - Location latitude
   * @param longitude - Location longitude
   * @param imageBase64 - Optional base64 image for face detection
   * @returns Promise<AttendanceMarkResult>
   */
  static async markEntry(punchCode: string, latitude: number, longitude: number, imageBase64?: string): Promise<AttendanceMarkResult> {
    if (imageBase64) {
      return this.markAttendanceWithFaceDetection({
        punchCode,
        latitude,
        longitude,
        deviceType: "1", // Default device type for web
        imageBase64,
      });
    } else {
      return this.markAttendanceWithLiveness({
        punchCode,
        latitude,
        longitude,
        deviceType: "1", // Default device type for web
      });
    }
  }

  /**
   * Mark exit with face detection and liveness verification
   * @param punchCode - Employee punch code
   * @param latitude - Location latitude
   * @param longitude - Location longitude
   * @param imageBase64 - Optional base64 image for face detection
   * @returns Promise<AttendanceMarkResult>
   */
  static async markExit(punchCode: string, latitude: number, longitude: number, imageBase64?: string): Promise<AttendanceMarkResult> {
    if (imageBase64) {
      return this.markAttendanceWithFaceDetection({
        punchCode,
        latitude,
        longitude,
        deviceType: "1", // Default device type for web
        imageBase64,
      });
    } else {
      // For exit, use FaceDetectionService.markExit method
      try {
        const exitResult = await FaceDetectionService.markExit(punchCode, latitude, longitude);
        return {
          success: exitResult.status === 'success',
          message: exitResult.message,
          data: exitResult.data,
        };
      } catch (error: any) {
        return {
          success: false,
          message: error.message || 'Failed to mark exit',
        };
      }
    }
  }
}

export default EnhancedAttendanceService;